import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CommonLayout from "../../layouts/CommonLayout";
import DynamicKakaoMapSelectContainer from "../../containers/DynamicKakaoMapSelectContainer";
import ImageUploadContainer from "../../containers/ImageUploadContainer";
import useAuthStore from "../../../store/authStore";
import { uploadImage, deleteImage } from "../../../api/ImageApi";
import { submitPartnerPlaceApplication } from "../../../api/PartnerPlaceApplicationApi";
import { AlertModal } from "../../layouts/AlertModal"; // AlertModal import

const PartnerPlaceFormPage = () => {
    const { member } = useAuthStore();
    const navigate = useNavigate();
    const mapRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [imageUrl, setImageUrl] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        phone: '',
        latitude: null,
        longitude: null,
        policy: '',
        imageId: null,
    });

    // 모달 상태 관리
    const [alertState, setAlertState] = useState({ isVisible: false, message: '', callback: null });

    const getErrorMessage = (error) => {
        if (error.response && error.response.data && error.response.data.message) {
            return error.response.data.message;
        }
        return '오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
    };

    const updateFormData = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setIsLoading(true);
            if (formData.imageId) {
                await deleteImage(formData.imageId);
                setImageUrl(null);
            }

            const response = await uploadImage(file);
            updateFormData('imageId', response.id);
            setImageUrl(response.url);
        } catch (error) {
            setAlertState({ isVisible: true, message: '파일 업로드에 실패했습니다. 파일은 10MB 이하로 업로드해주세요.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleImageDelete = () => {
        updateFormData('imageId', null);
        setImageUrl(null);
    };

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            setAlertState({ isVisible: true, message: '매장 이름을 입력해주세요.' });
            return;
        }
        if (!formData.address.trim()) {
            setAlertState({ isVisible: true, message: '매장 주소를 입력해주세요.' });
            return;
        }
        if (!formData.phone.trim()) {
            setAlertState({ isVisible: true, message: '매장 전화번호를 입력해주세요.' });
            return;
        }
        if (!formData.policy.trim()) {
            setAlertState({ isVisible: true, message: '매장 제휴 혜택을 입력해주세요.' });
            return;
        }

        const currentPosition = mapRef.current?.getCurrentPosition() || {
            latitude: member?.latitude,
            longitude: member?.longitude
        };

        if (!currentPosition.latitude || !currentPosition.longitude) {
            setAlertState({ isVisible: true, message: '매장 위치를 선택해주세요.' });
            return;
        }

        const applicationData = {
            name: formData.name,
            address: formData.address,
            phone: formData.phone,
            latitude: currentPosition.latitude,
            longitude: currentPosition.longitude,
            policy: formData.policy,
            imageId: formData.imageId
        };

        try {
            setIsLoading(true);
            await submitPartnerPlaceApplication(applicationData);
            setAlertState({
                isVisible: true,
                message: '제휴매장 신청이 완료되었습니다. \n검토 후 연락드리겠습니다.',
                callback: () => navigate('/app/mypage')
            });
        } catch (error) {
            console.error('제휴매장 신청 실패:', error);
            setAlertState({ isVisible: true, message: getErrorMessage(error) });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <CommonLayout title="밥친구 제휴 매장 신청">
            <AlertModal
                isVisible={alertState.isVisible}
                message={alertState.message}
                callback={alertState.callback}
                onClose={() => setAlertState({ isVisible: false, message: '', callback: null })}
            />
            <div className="space-y-4 p-4 max-w-lg mx-auto">
                {/* 환영 메시지 */}
                <h1 className="text-xl font-semibold">
                    밥친구와 함께 <br/>
                    우리 매장을 효과적으로 홍보해보세요!
                </h1>
                {/* 매장 이름 입력 필드 */}
                <div className="text-sm font-semibold">매장 이름</div>
                <div>
                    <input
                        type="text"
                        placeholder="매장 이름"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                        maxLength="20"
                        value={formData.name}
                        onChange={(e) => updateFormData('name', e.target.value)}
                    />
                </div>
                {/* 매장 주소 입력 필드 */}
                <div className="text-sm font-semibold">매장 주소</div>
                <div>
                    <input
                        type="text"
                        placeholder="매장 주소"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                        maxLength="100"
                        value={formData.address}
                        onChange={(e) => updateFormData('address', e.target.value)}
                    />
                </div>
                {/* 매장 전화번호 입력 필드 */}
                <div className="text-sm font-semibold">매장 전화번호</div>
                <div>
                    <input
                        type="text"
                        placeholder="매장 전화번호 ( - 포함 입력 )"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                        maxLength="15"
                        value={formData.phone}
                        onChange={(e) => updateFormData('phone', e.target.value)}
                    />
                </div>

                {/* 매장 위치 선택 */}
                <div className="text-sm font-semibold">매장 위치 (지도를 움직여서 선택해주세요)</div>
                <div className="border border-gray-300 h-64 overflow-hidden">
                    <DynamicKakaoMapSelectContainer
                        ref={mapRef}
                        className="w-full h-full"
                    />
                </div>

                {/* 매장 제휴 혜택 입력 필드 */}
                <div className="text-sm font-semibold">매장 제휴 혜택</div>
                <div>
                    <textarea
                        placeholder="매장 제휴 혜택"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg h-40"
                        maxLength="300"
                        value={formData.policy}
                        onChange={(e) => updateFormData('policy', e.target.value)}
                    />
                </div>

                {/* 제휴 매장 이미지 업로드 */}
                <div className="text-sm font-semibold">매장 소개 사진</div>
                <div className="border border-gray-300">
                    <ImageUploadContainer
                        imageUrl={imageUrl}
                        onUpload={handleImageUpload}
                        onDelete={handleImageDelete}
                        isLoading={isLoading}
                    />
                </div>

                <div className="p-4 bg-gray-100 rounded-md">
                    <ul className="list-disc pl-5 text-black text-sm">
                        <li>제출해주신 내용은 밥친구 관리자가 검토 후 세부적인 내용에 대해 연락드리겠습니다.</li>
                    </ul>
                </div>

                <button
                    className="w-full py-3 bg-amber-500 text-white rounded-lg disabled:bg-gray-400"
                    onClick={handleSubmit}
                    disabled={isLoading}
                >
                    {isLoading ? '신청 중...' : '제휴 매장 신청'}
                </button>
            </div>
        </CommonLayout>
    );
};

export default PartnerPlaceFormPage;
